"use client";

import { useEffect, useRef, useState } from "react";
import { useRouter } from "next/navigation";
import { Loader2, ClipboardPaste, CheckCircle2, AlertCircle, KeyRound } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { createClient, isSupabaseConfigured } from "@/lib/supabase/client";
import { useLocale, useT } from "@/lib/use-i18n";
import { localePath } from "@/lib/i18n/locales";
import { toast } from "sonner";
function extractCode(input: string): string | null {
  const trimmed = input.trim();
  if (!trimmed) return null;
  // If input is a full URL, extract ?code= param or #code= hash
  try {
    if (trimmed.startsWith("http://") || trimmed.startsWith("https://") || trimmed.includes("code=")) {
      // Try to parse as URL
      const url = new URL(trimmed.startsWith("http") ? trimmed : `https://dummy.com?${trimmed}`);
      const qCode = url.searchParams.get("code");
      if (qCode) return qCode.trim();
      // Check hash
      if (url.hash) {
        const hashParams = new URLSearchParams(url.hash.slice(1));
        const hCode = hashParams.get("code");
        if (hCode) return hCode.trim();
      }
      // Also try raw search string path like https://www.vortixy.net/es?code=xxx
      const match = trimmed.match(/[?&]code=([^&#\s]+)/);
      if (match?.[1]) {
        try {
          return decodeURIComponent(match[1]).trim();
        } catch {
          return match[1].trim();
        }
      }
    }
  } catch {}
  // Otherwise treat whole input as code (strip whitespace)
  // Code is typically UUID or long string without spaces
  const codeOnly = trimmed.split(/\s+/)[0] ?? "";
  if (codeOnly.length >= 8 && codeOnly.length < 2048) {
    return codeOnly;
  }
  return null;
}
export function DeviceCodeLogin() {
  const t = useT();
  // 2026-09-28 D4-09: los 2 redirects de éxito iban a "/chat" sin prefijo de
  // locale ⇒ un usuario DE/KO/AR caía en la versión EN (y en `ar` perdía
  // `dir="rtl"`). research 2026-09-28 AGENTS.md § i18n (SSOT locale-routes) +
  // Google Search Central localized-versions. 0 cambios visuales.
  const locale = useLocale();
  const router = useRouter();
  const [codeInput, setCodeInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const formRef = useRef<HTMLFormElement>(null);
  const errorMsgRef = useRef<HTMLDivElement>(null);

  // 2026-09-22 FASE 3 FIX a11y (research 2026 WCAG 2.2 3.3.1 + AuthForm:141
  // parity): el error recibe foco; role="alert" pre-existente lo anuncia.
  useEffect(() => {
    if (error) {
      errorMsgRef.current?.focus({
        preventScroll: false
      });
    }
  }, [error]);
  const handlePaste = async () => {
    try {
      const text = await navigator.clipboard.readText();
      if (text) setCodeInput(text.trim());
    } catch {
      // clipboard read may be blocked — user can paste manually
    }
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!isSupabaseConfigured()) {
      setError(t("authErrorNotActive"));
      return;
    }
    const code = extractCode(codeInput);
    if (!code) {
      setError(t("authDeviceCodeInvalid") ?? "Introduce un código válido.");
      return;
    }

    // Quick-login code path: 8-char base62 → our 5-min one-time code
    const isQuickCode = /^[A-Za-z0-9]{8}$/.test(code);
    setLoading(true);
    try {
      if (isQuickCode) {
        const res = await fetch("/api/auth/quick-verify", {
          method: "POST",
          headers: {
            "Content-Type": "application/json"
          },
          body: JSON.stringify({
            code
          })
        });
        if (res.ok) {
          const j = (await res.json().catch(() => null)) as {
            actionLink?: string;
            ok?: boolean;
          } | null;
          setSuccess(true);
          toast.success(t("authDeviceCodeSuccess") ?? "Sesión iniciada. Redirigiendo…");
          if (j?.actionLink) {
            window.location.href = j.actionLink;
            return;
          }
          window.setTimeout(() => {
            router.push(localePath(locale, "/chat"));
            router.refresh();
          }, 600);
          return;
        }
        // Map error
        if (res.status === 429) {
          setError(t("authErrorRateLimit"));
          return;
        }
        setError(t("authDeviceCodeError") ?? "Código inválido o vencido. Solicita uno nuevo iniciando de nuevo con Google.");
        return;
      }
      const supabase = createClient();
      const {
        error: exchangeError
      } = await supabase.auth.exchangeCodeForSession(code);
      if (exchangeError) {
        // Try to map common errors
        const msg = exchangeError.message?.toLowerCase() ?? "";
        if (msg.includes("expired") || msg.includes("invalid") || msg.includes("code")) {
          setError(t("authDeviceCodeError") ?? "Código inválido o vencido. Solicita uno nuevo iniciando de nuevo con Google.");
        } else {
          setError(exchangeError.message);
        }
        return;
      }
      setSuccess(true);
      toast.success(t("authDeviceCodeSuccess") ?? "Sesión iniciada. Redirigiendo…");
      // Give a moment for toast, then redirect
      window.setTimeout(() => {
        router.push(localePath(locale, "/chat"));
        router.refresh();
      }, 600);
    } catch {
      setError(t("authErrorGeneric"));
    } finally {
      setLoading(false);
    }
  };
  if (success) {
    return <div role="status" className="flex items-start gap-2 rounded-[var(--radius-panel)] border border-[var(--success)]/20 bg-[var(--success)]/10 p-3 text-sm text-[var(--success-text)]">
        <CheckCircle2 className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
        <span>
          {t("authDeviceCodeSuccess") ?? "Sesión iniciada. Redirigiendo…"}
        </span>
      </div>;
  }
  return (
    // 2026-09-25 FASE 3 FIX design-docs-auth (research 2026 single-glass:
    // este form vive DENTRO del lovable-card de AuthForm:618 — la card
    // interna duplicaba borde+sombra+brillo. Disclosure sin chrome propio:
    // separador hairline + padding, 1 sola superficie).
    <form ref={formRef} onSubmit={handleSubmit} className="space-y-3 border-t border-[var(--border)] pt-5 v-rise">
      <div className="flex items-start gap-2.5">
        <span className="mt-0.5 inline-flex size-7 shrink-0 items-center justify-center rounded-xl border border-border/50 bg-muted/50">
          <KeyRound className="size-3.5 text-muted-foreground" aria-hidden="true" />
        </span>
        <div className="min-w-0 flex-1">
          <h3 className="text-[13px] font-semibold tracking-tight text-foreground">
            {t("authDeviceCodeTitle") ?? "¿Tienes un código de inicio?"}
          </h3>
          {/* 2026-09-23 FASE 3 FIX diseño (research 2026 WCAG 2.2 1.4.3 AA:
              11px atenuado no llega a AA — sube a 12px + fg pleno). */}
          <p className="mt-0.5 text-xs leading-4 text-muted-foreground">
            {t("authDeviceCodeDesc") ?? "Pega el código que copiaste del navegador para completar el inicio de sesión en esta app."}
          </p>
        </div>
      </div>

      <div className="space-y-2">
        <label htmlFor="device-code-input" className="block text-xs font-medium tracking-wide text-foreground">
          {t("authDeviceCodePasteLabel") ?? "Código"}
        </label>
        {/* 2026-09-21 FASE 3 FIX design-auth-admin: columna en angosto
            (a 360px el input quedaba <200px junto a "Pegar") → fila desde sm. */}
        <div className="flex flex-col gap-2 sm:flex-row">
          <Input id="device-code-input" type="text" autoComplete="one-time-code" spellCheck={false} placeholder={t("authDeviceCodePlaceholder") ?? "Pega el código o la URL completa aquí"} value={codeInput} onChange={e_0 => setCodeInput(e_0.target.value)}
          // 2026-09-26 FASE 4 MEJORA transversal RONDA EXTRA M2 (research
          // 2026 MDN autocapitalize 2026-04-17 + Twilio OTP forms 2026: el
          // teclado iOS capitaliza la 1ª letra y autocorrige por defecto —
          // en un código base62/UUID eso corrompe lo tecleado (el pegado
          // manual ya estaba permitido, WCAG 2.2 3.3.8). none/off lo
          // blindan; autoComplete one-time-code + spellCheck=false intactos.
          // Sin cambio visual, sin copy, 0 controles nuevos.)
          autoCapitalize="none" autoCorrect="off"
          // 2026-09-23 FASE 3 FIX diseño (research 2026 M3 Expressive + WCAG 2.2 2.5.8:
          // h-10 40px → h-11 44px + radius-field 16px SSOT, paridad fields del sistema).
          className="h-11 flex-1 rounded-[var(--radius-field)] border-border/60 bg-muted/20 font-mono text-xs tracking-wider placeholder:font-sans placeholder:tracking-normal"
          // 2026-09-24 FASE 4 MEJORA transversal M2 (research 2026 W3C
          // WCAG 2.2 ARIA21: omitir el atributo sin error en vez de
          // aria-invalid="false" — paridad ContactForm/SuggestionForm/
          // StatusDashboard `? true : undefined`; "false" es ruido AT.
          // Sin cambio visual.)
          aria-invalid={error ? true : undefined} aria-describedby={error ? "device-code-error" : "device-code-hint"} />
          {/* 2026-09-21 FASE 3 FIX design-auth-admin: "Pegar" hardcodeado ES
              → clave i18n authDeviceCodePaste (14 locales). Pegado manual
              intacto (WCAG 2.2 3.3.8: sin bloqueo de pegado). */}
          <Button type="button" variant="outline" onClick={handlePaste}
          // 2026-09-23 FASE 3 FIX diseño (research 2026 DSP radius tokens:
          // rounded-xl → token --radius-button pill, paridad botón del sistema).
          className="h-11 w-full shrink-0 justify-center gap-1.5 rounded-[var(--radius-button)] border-border/60 bg-muted/30 text-xs hover:bg-muted/60 sm:w-auto" aria-label={t("authDeviceCodePaste") ?? "Pegar"}>
            <ClipboardPaste className="size-3.5" aria-hidden="true" />
            {t("authDeviceCodePaste") ?? "Pegar"}
          </Button>
        </div>
        <p id="device-code-hint" className="text-xs leading-4 text-muted-foreground">
          {t("authDeviceCodeHint") ?? "Puedes pegar el enlace completo (https://…?code=…) o solo el código."}
        </p>
      </div>

      {error ? <div id="device-code-error" ref={errorMsgRef} role="alert" tabIndex={-1} className="flex items-start gap-2 rounded-[var(--radius-panel)] border border-[var(--danger)]/20 bg-[var(--danger)]/10 p-2.5 text-xs text-[var(--danger-text)] outline-none">
          <AlertCircle className="mt-0.5 size-3.5 shrink-0" aria-hidden="true" />
          <span>{error}</span>
        </div> : null}

      {/* 2026-09-21 FASE 3 FIX design-auth-admin: submit secundario (outline) —
          con el panel abierto el único primario es el del formulario email. */}
      {/* 2026-09-23 FASE 3 FIX diseño (research 2026 DSP radius tokens:
          rounded-xl → token --radius-button pill, paridad botón del sistema). */}
      <Button type="submit" className="h-11 w-full gap-2 rounded-[var(--radius-button)]" variant="outline" disabled={loading || !codeInput.trim()} aria-busy={loading}>
        {loading ? <Loader2 className="size-4 animate-spin motion-reduce:animate-none" aria-hidden="true" /> : null}
        {t("authDeviceCodeButton") ?? "Continuar con código"}
      </Button>
    </form>
  );
}